import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';

const BrushableImage = ({
  src,
  brushMode,
  brushSize,
  onBrushStroke,
  className = '',
  onBrushSizeChange,
  onImageSize,
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentStroke, setCurrentStroke] = useState([]);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const computeBrushPixels = useCallback((units) => {
    if (!imageSize.width || !imageSize.height) {
      return Math.max(units, 1);
    }

    const reference = Math.min(imageSize.width, imageSize.height);
    const normalizedUnits = Math.min(Math.max(units, 1), 100);
    const pixels = (normalizedUnits / 100) * reference;
    const minPixels = Math.max(reference * 0.01, 3);
    return Math.max(pixels, minPixels);
  }, [imageSize]);

  const brushPixels = useMemo(() => computeBrushPixels(brushSize), [brushSize, computeBrushPixels]);

  useEffect(() => {
    if (typeof onBrushSizeChange === 'function') {
      onBrushSizeChange(brushPixels);
    }
  }, [brushPixels, onBrushSizeChange]);

  // Draw the base image when src changes
  useEffect(() => {
    if (!src || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Set canvas size to match image
      canvas.width = img.width;
      canvas.height = img.height;
      
      // Clear and draw image
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      setImageSize({ width: img.width, height: img.height });
      if (typeof onImageSize === 'function') {
        try {
          onImageSize({ width: img.width, height: img.height });
        } catch {
          /* no-op: parent callback is best-effort */
        }
      }
    };
    
    img.src = src;
  }, [src, onImageSize]);

  // Get mouse/touch position relative to canvas
  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX || (e.touches && e.touches[0]?.clientX);
    const clientY = e.clientY || (e.touches && e.touches[0]?.clientY);
    
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
      size: brushPixels
    };
  };

  // Start drawing
  const startDrawing = (e) => {
    if (!brushMode) return;
    
    setIsDrawing(true);
    const pos = getEventPos(e);
    setCurrentStroke([pos]);
    
    // Draw initial point
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)'; // Semi-transparent white
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.lineWidth = brushPixels;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushPixels / 2, 0, 2 * Math.PI);
    ctx.fill();
  };

  // Continue drawing
  const draw = (e) => {
    if (!brushMode || !isDrawing) return;
    
    e.preventDefault(); // Prevent scrolling on mobile
    
    const pos = getEventPos(e);
    const newStroke = [...currentStroke, pos];
    setCurrentStroke(newStroke);
    
    // Draw line from previous point
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (currentStroke.length > 0) {
      const prevPos = currentStroke[currentStroke.length - 1];
      ctx.beginPath();
      ctx.lineWidth = brushPixels;
      ctx.moveTo(prevPos.x, prevPos.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    }
    
    // Draw current point
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, brushPixels / 2, 0, 2 * Math.PI);
    ctx.fill();
  };

  // Stop drawing
  const stopDrawing = () => {
    if (!brushMode || !isDrawing) return;
    
    setIsDrawing(false);
    
    if (currentStroke.length > 0 && onBrushStroke) {
      onBrushStroke(currentStroke);
    }
    
    setCurrentStroke([]);
  };

  return (
    <canvas
      ref={canvasRef}
      className={`${className} ${brushMode ? 'cursor-crosshair' : 'cursor-default'}`}
      style={{
        border: '1px solid #ddd',
        maxWidth: '100%',
        height: 'auto'
      }}
      onMouseDown={startDrawing}
      onMouseMove={draw}
      onMouseUp={stopDrawing}
      onMouseLeave={stopDrawing}
      onTouchStart={startDrawing}
      onTouchMove={draw}
      onTouchEnd={stopDrawing}
    />
  );
};

export default BrushableImage;
